import React, { useEffect } from 'react';
import { Content } from '@leverege/ui-elements';
import { TitleBar } from '@leverege/ui-plugin';
import WizardContext from "./WizardContext.js";
import useWizardInternals from "./useWizardInternals.js";
/**
 * @typedef {Object} TWizardProps
 * @property {string} className The className to apply to the wizard's container Content element
 * @property {string} contentClassName The className to apply to the wizard's Content.Area element
 * @property {string} variant The variant to be applied to the wizard's Content component. If no `contentVariant`, 
 * `footerVariant` or `headerVariant` are supplied, their variants will be a concatencation of this variant and "Area", "Footer" or "Header" 
 * respectively.
 * @property {string} contentVariant The variant to be applied to the wizard's Content.Area component.
 * @property {any} context An arbitrary context value that will be supplied to child components through the [WizardContext]{@link TWizardContext}
 * @property {string} footerClassName The className to apply to the wizard's Content.Footer component
 * @property {string} footerLayout The layout to apply to the wizard's Content.Footer component
 * @property {string} footerVariant The variant to apply to the wizard's Content.Footer component
 * @property {string} headerClassName The className to apply to the wizard's Content.Header component
 * @property {string} headerVariant The variant to apply to the wizard's Content.Header component
 * @property {boolean} mobile Whether or not the wizard is displayed in a mobile UI. If false, the positioning of the wizard's Content 
 * component will be relative
 * @property {Object} model The json model representing the steps in the wizard and various other configuration options. See {@tutorial getting-started} for more information.
 * @property {import('./WizardContext').ChangeFunction} onChange In concert with the `value` prop, can be used by child steps to edit the "value" of the wizard (accessible in the wizardData property of the [WizardContext]{@link TWizardContext})
 * @property {Function} onReset If this function is passed to the wizard, any calls to the [WizardContext]{@link TWizardContext} `onReset` function will both reset the wizard to its initial step AND will call this function in turn to allow for any additional cleanup.
 * @property {import('react').ReactElement} titleBarExtra Optional component to render as a child of the Content.Header's TitleBar component.
 * @property {any} value The value represented by the wizard flow. Can be alerted in conjuction with the onChange function (if supplied).
 */
/**
 * @memberof UIWizard
 * @function Wizard
 * @description The wrapper component used to render a wizard flow and provide a WizardContext to child components
 * @type {import('react').FunctionComponent<TWizardProps>}
 */
export default function Wizard(props) {
  const {
    className,
    contentClassName,
    variant,
    contentVariant = `${variant}Area`,
    context,
    footerClassName,
    footerLayout = 'flex:rowMEnd',
    footerVariant = `${variant}Footer`,
    headerClassName,
    headerVariant = `${variant}Header`,
    mobile,
    model,
    onChange,
    onReset,
    titleBarExtra,
    value
  } = props;
  const {
    content,
    footer,
    header,
    headerIcon,
    wizardContext,
    wizardId
  } = useWizardInternals({
    context,
    model,
    onChange,
    onReset,
    value
  });
  return /*#__PURE__*/React.createElement(WizardContext.Provider, {
    value: wizardContext
  }, /*#__PURE__*/React.createElement(Content, {
    id: wizardId,
    relative: !mobile,
    className: className,
    variant: variant
  }, header && /*#__PURE__*/React.createElement(Content.Header, {
    variant: headerVariant,
    className: headerClassName
  }, /*#__PURE__*/React.createElement(TitleBar, {
    controlsVariant: "pairTitleControls",
    variant: "dialogTitle",
    title: header,
    icon: headerIcon
  }, titleBarExtra)), content && /*#__PURE__*/React.createElement(Content.Area, {
    variant: contentVariant,
    className: contentClassName
  }, content), footer && /*#__PURE__*/React.createElement(Content.Footer, {
    variant: footerVariant,
    className: footerClassName,
    layout: footerLayout
  }, footer)));
}